import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnalysisResult } from '../types/analysis';
import { 
  CheckCircle2, 
  AlertCircle, 
  HelpCircle,
  Image,
  ChevronDown,
  ChevronUp,
  Smartphone,
  Sparkles,
  Pencil,
  FileJson,
  Clock,
  Download,
  AlertTriangle
} from 'lucide-react';

interface AnalysisResultsProps {
  result: AnalysisResult;
  preview?: string;
}

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({ result, preview }) => {
  const [showDetails, setShowDetails] = useState(false);

  const getVerdict = () => {
    const riskScore = result.risk_score;
    
    // Professional verdicts with actionable guidance
    if (result.source === 'REAL_CAMERA' && riskScore < 20) {
      return {
        title: 'Verified Authentic',
        subtitle: "Original camera photo with complete metadata chain.",
        icon: <CheckCircle2 className="w-16 h-16 text-[#1F4FFF]" />,
        bgColor: 'bg-[#F1F4F8]',
        borderColor: 'border-[#D0D5DD]',
        textColor: 'text-[#0A1A2F]'
      };
    } else if (result.source === 'SNAPCHAT') {
      return {
        title: 'Snapchat Processed',
        subtitle: "Authentic photo captured through Snapchat camera with filters applied.",
        icon: <CheckCircle2 className="w-16 h-16 text-[#FFFC00]" />,
        bgColor: 'bg-[#FFFBEB]',
        borderColor: 'border-[#FDE047]',
        textColor: 'text-[#0A1A2F]'
      };
    } else if (result.source === 'GOOGLE_DOWNLOAD') {
      return {
        title: 'Web Downloaded Image',
        subtitle: "Image downloaded from web source. Request original photo from sender for verification.",
        icon: <AlertTriangle className="w-16 h-16 text-[#F97316]" />,
        bgColor: 'bg-[#FFF7ED]',
        borderColor: 'border-[#FDBA74]',
        textColor: 'text-[#0A1A2F]'
      };
    } else if (result.source === 'LIKELY_AI' || riskScore >= 70) {
      return {
        title: 'High Risk - Likely AI Generated',
        subtitle: "Strong indicators of AI generation or significant manipulation detected.",
        icon: <AlertCircle className="w-16 h-16 text-[#EF4444]" />,
        bgColor: 'bg-[#FEE2E2]',
        borderColor: 'border-[#FCA5A5]',
        textColor: 'text-[#0A1A2F]'
      };
    } else if (riskScore >= 50) {
      return {
        title: 'Manual Review Recommended',
        subtitle: "Multiple risk indicators detected. Review the details below to make an informed decision.",
        icon: <AlertTriangle className="w-16 h-16 text-[#F59E0B]" />,
        bgColor: 'bg-[#FEF3C7]',
        borderColor: 'border-[#FCD34D]',
        textColor: 'text-[#0A1A2F]'
      };
    } else if (riskScore >= 30) {
      return {
        title: 'Low Risk - Minor Flags',
        subtitle: "Some metadata inconsistencies detected. Review details below before making a decision.",
        icon: <HelpCircle className="w-16 h-16 text-[#FACC15]" />,
        bgColor: 'bg-[#F8FAFD]',
        borderColor: 'border-[#D0D5DD]',
        textColor: 'text-[#0A1A2F]'
      };
    } else {
      return {
        title: 'Low Risk Score',
        subtitle: "No major red flags detected, but this doesn't guarantee authenticity. Use your judgment based on context.",
        icon: <CheckCircle2 className="w-16 h-16 text-[#16A34A]" />,
        bgColor: 'bg-[#DCFCE7]',
        borderColor: 'border-[#86EFAC]',
        textColor: 'text-[#0A1A2F]'
      };
    }
  };

  const verdict = getVerdict();

  const getEdgeCaseDetails = () => {
    const details: Array<{ icon: React.ReactNode; message: string; type: string }> = [];

    // Screenshot of AI detection (CRITICAL - show first)
    if (result.flags.includes('SCREENSHOT_OF_AI_SUSPECTED') || 
        result.flags.includes('SCREENSHOT_OF_AI_HIGH_CONFIDENCE') ||
        result.flags.includes('SCREENSHOT_SYNTHETIC_COLORS')) {
      details.push({
        icon: <AlertCircle className="w-5 h-5 text-[#EF4444]" />,
        message: '⚠️ Screenshot of AI image detected - attempting to mask AI origin',
        type: 'screenshot_of_ai'
      });
    }
    // Regular screenshot detection
    else if (result.flags.includes('SCREENSHOT_PATTERN')) {
      details.push({
        icon: <Smartphone className="w-5 h-5 text-[#1F4FFF]" />,
        message: 'Possible screenshot detected',
        type: 'screenshot'
      });
    }

    // AI generation with software name
    if (result.flags.includes('AI_METADATA_DETECTED')) {
      const software = result.exif_summary.software;
      if (software) {
        details.push({
          icon: <Sparkles className="w-5 h-5 text-[#1F4FFF]" />,
          message: `AI generation detected: ${software}`,
          type: 'ai_software'
        });
      } else {
        details.push({
          icon: <Sparkles className="w-5 h-5 text-[#1F4FFF]" />,
          message: 'AI generation signs detected',
          type: 'ai_markers'
        });
      }
    }

    // Processing/editing software
    if (result.flags.includes('PROCESSING_SOFTWARE_DETECTED') || result.flags.includes('SUSPICIOUS_SOFTWARE_TAG')) {
      const software = result.exif_summary.software;
      if (software && !result.flags.includes('AI_METADATA_DETECTED')) {
        details.push({
          icon: <Pencil className="w-5 h-5 text-[#1F4FFF]" />,
          message: `Edited with: ${software}`,
          type: 'editing'
        });
      }
    }

    // No camera data
    if (result.flags.includes('NO_CAMERA_HARDWARE_EXIF')) {
      details.push({
        icon: <Image className="w-5 h-5 text-[#1F4FFF]" />,
        message: 'No camera information found',
        type: 'no_camera'
      });
    }

    // Incomplete camera data
    if (result.flags.includes('LIMITED_CAMERA_EXIF')) {
      details.push({
        icon: <Image className="w-5 h-5 text-[#1F4FFF]" />,
        message: 'Limited camera information',
        type: 'limited_camera'
      });
    }

    // Low quality / re-encoding
    if (result.flags.includes('LOW_JPEG_QUALITY')) {
      details.push({
        icon: <FileJson className="w-5 h-5 text-[#1AC8E3]" />,
        message: 'Image has been re-compressed',
        type: 'quality'
      });
    }

    // Missing PNG metadata
    if (result.flags.includes('MISSING_PNG_METADATA')) {
      details.push({
        icon: <FileJson className="w-5 h-5 text-[#1AC8E3]" />,
        message: 'Missing PNG details',
        type: 'png_metadata'
      });
    }

    // No timestamp
    if (result.flags.includes('MISSING_DATETIME_ORIGINAL')) {
      details.push({
        icon: <Clock className="w-5 h-5 text-[#1F4FFF]" />,
        message: 'No original capture timestamp',
        type: 'no_timestamp'
      });
    }

    // Filename-based hints: WhatsApp / Instagram downloads
    if (result.flags.includes('LIKELY_WHATSAPP_DOWNLOAD')) {
      details.push({
        icon: <Download className="w-5 h-5 text-[#1F4FFF]" />,
        message: 'Seems like a WhatsApp-downloaded image',
        type: 'whatsapp_filename'
      });
    }

    if (result.flags.includes('LIKELY_INSTAGRAM_DOWNLOAD')) {
      details.push({
        icon: <Download className="w-5 h-5 text-[#1F4FFF]" />,
        message: 'Looks like an Instagram-downloaded image',
        type: 'instagram_filename'
      });
    }

    return details;
  };

  const getCameraInfo = () => {
    const { exif_summary } = result;
    if (exif_summary.make || exif_summary.model) {
      return `${exif_summary.make || ''} ${exif_summary.model || ''}`.trim();
    }
    return null;
  };

  const cameraInfo = getCameraInfo();
  const edgeCaseDetails = getEdgeCaseDetails();

  const getSourceHint = () => {
    if (result.flags.includes('LIKELY_WHATSAPP_DOWNLOAD')) {
      return 'Source hint: WhatsApp download';
    }
    if (result.flags.includes('LIKELY_INSTAGRAM_DOWNLOAD')) {
      return 'Source hint: Instagram download';
    }
    return null;
  };

  const sourceHint = getSourceHint();

  const getMarkerIcon = () => {
    if (result.source === 'LIKELY_AI') {
      return <AlertTriangle className="w-6 h-6 text-[#EF4444]" />;
    } else if (result.source === 'REAL_CAMERA') {
      return <CheckCircle2 className="w-6 h-6 text-[#16A34A]" />;
    } else {
      return <HelpCircle className="w-6 h-6 text-[#FACC15]" />;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-2xl mx-auto px-4 py-6 space-y-4"
    >
      {/* Image Preview with Marker, Score Badge, and Verdict */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-sm aspect-square bg-white rounded-3xl overflow-hidden border-2 border-[#D0D5DD] shadow-lg"
      >
        {/* Risk Score Badge - Top Left */}
        <div className={`absolute top-0 left-0 z-30 rounded-tl-3xl rounded-br-2xl px-4 py-3 shadow-lg ${
          result.risk_score >= 60 ? 'bg-red-500' : 
          result.risk_score >= 30 ? 'bg-amber-500' : 
          'bg-emerald-500'
        }`}>
          <p className="text-xs text-white/90 font-medium uppercase tracking-wide">Risk Score</p>
          <p className="text-3xl font-bold text-white">{result.risk_score}%</p>
        </div>

        {/* Marker Icon - Top Right */}
        <div className={`absolute top-3 right-3 z-10 rounded-full p-3 shadow-lg flex items-center justify-center ${
          result.source === 'LIKELY_AI' ? 'bg-red-500' :
          result.source === 'REAL_CAMERA' ? 'bg-emerald-500' :
          result.source === 'SNAPCHAT' ? 'bg-yellow-400' :
          result.source === 'GOOGLE_DOWNLOAD' ? 'bg-orange-500' :
          'bg-amber-500'
        }`}>
          {result.source === 'LIKELY_AI' ? (
            <AlertTriangle className="w-6 h-6 text-white" />
          ) : result.source === 'REAL_CAMERA' ? (
            <CheckCircle2 className="w-6 h-6 text-white" />
          ) : result.source === 'SNAPCHAT' ? (
            <CheckCircle2 className="w-6 h-6 text-black" />
          ) : result.source === 'GOOGLE_DOWNLOAD' ? (
            <AlertTriangle className="w-6 h-6 text-white" />
          ) : (
            <HelpCircle className="w-6 h-6 text-white" />
          )}
        </div>

        {/* Image */}
        {preview ? (
          <img
            src={preview}
            alt="Analyzed"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#F1F4F8] to-[#F1F4F8] flex items-center justify-center">
            <div className="text-center">
              <Image className="w-16 h-16 text-[#1F4FFF] mx-auto mb-2 opacity-50" />
              <p className="text-sm text-[#475467]">
                {result.exif_summary.width && result.exif_summary.height 
                  ? `${result.exif_summary.width}×${result.exif_summary.height}`
                  : 'No image data'
                }
              </p>
            </div>
          </div>
        )}

        {/* Bottom Overlay - Verdict, AI Probability, Recommended Action */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-4 text-white">
          {/* Verdict Title */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold">{verdict.title}</h3>
          </div>
          
          {/* AI Probability & Recommended Action Row */}
          <div className="flex items-center justify-between gap-3">
            {/* AI Probability */}
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-300" />
              <span className="text-sm font-medium">
                AI Probability: <span className="font-bold text-lg">{result.ai_probability ?? 0}%</span>
              </span>
            </div>
            
            {/* Recommended Action Badge */}
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg ${
              result.recommended_action === 'AUTO_PASS' 
                ? 'bg-emerald-500 text-white' 
                : result.recommended_action === 'AUTO_FAIL'
                ? 'bg-red-500 text-white'
                : 'bg-amber-500 text-white'
            }`}>
              {result.recommended_action === 'AUTO_PASS' ? 'PASS' : 
               result.recommended_action === 'AUTO_FAIL' ? 'FAIL' : 
               'REVIEW'}
            </div>
          </div>

          {/* Source Hint */}
          {sourceHint && (
            <p className="text-xs mt-2 opacity-75">{sourceHint}</p>
          )}
        </div>
      </motion.div>


      {/* Camera Info */}
      {cameraInfo && (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-md border border-[#D0D5DD]"
        >
          <div>
            <p className="text-sm text-[#475467]">Captured with</p>
            <p className="text-lg font-semibold text-[#0A1A2F]">{cameraInfo}</p>
          </div>
        </motion.div>
      )}

      {/* Edge Case Details */}
      {edgeCaseDetails.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-md border border-[#D0D5DD]"
        >
          <h3 className="text-lg font-semibold text-[#0A1A2F] mb-4">
            Detection Details
          </h3>
          <div className="space-y-3">
            <AnimatePresence>
              {edgeCaseDetails.slice(0, showDetails ? edgeCaseDetails.length : 3).map((detail, index) => (
                <motion.div 
                  key={detail.type}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center space-x-3 p-4 bg-white rounded-3xl border border-[#D0D5DD]"
                >
                  <div className="flex-shrink-0">
                    {detail.icon}
                  </div>
                  <p className="text-[#0A1A2F] text-sm font-medium leading-relaxed">{detail.message}</p>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {edgeCaseDetails.length > 3 && (
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full py-3 text-sm text-[#1F4FFF] hover:text-[#163ACC] font-medium flex items-center justify-center space-x-1 hover:bg-[#E5EDFF] rounded-3xl transition-colors"
              >
                <span>{showDetails ? 'Show less' : `Show ${edgeCaseDetails.length - 3} more`}</span>
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* No Issues Found */}
      {edgeCaseDetails.length === 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-[#F1F4F8] rounded-3xl p-6 shadow-md border border-[#D0D5DD]"
        >
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-[#16A34A]" />
            <div>
              <p className="font-semibold text-[#0A1A2F]">No suspicious indicators found</p>
              <p className="text-sm text-[#475467]">This image passed all authenticity checks</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Image Dimensions */}
      {result.exif_summary.width && result.exif_summary.height && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-3xl p-6 shadow-md border border-[#D0D5DD]"
        >
          <div className="flex items-center justify-between">
            <span className="text-[#0A1A2F] font-medium">Image Size</span>
            <span className="text-lg font-semibold text-[#0A1A2F]">
              {result.exif_summary.width} × {result.exif_summary.height}
            </span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AnalysisResults;
